import { Injectable } from '@angular/core';

/** Intrinsic media size at scale 1, rotation 0. */
export interface MediaSize {
  width: number;
  height: number;
}

/** Size of the stage the media must fit into. */
export interface StageSize {
  width: number;
  height: number;
}

/**
 * Pure zoom mathematics for the lightbox image — no state, no DOM. Kept separate so the
 * fit/clamp/step rules are unit-testable in isolation. Mirrors `PdfZoomService`.
 */
@Injectable({ providedIn: 'root' })
export class MediaZoomService {
  /** Padding (px) subtracted from the stage so a fitted image never touches the edges. */
  private readonly gutter = 48;

  /** Clamp a zoom percentage into the `[min, max]` range. */
  clamp(zoom: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, Math.round(zoom)));
  }

  /** Next zoom level when zooming in, respecting the step and ceiling. */
  zoomIn(zoom: number, step: number, max: number): number {
    return this.clamp(Math.max(zoom + 1, zoom * step), zoom, max);
  }

  /** Next zoom level when zooming out, respecting the step and floor. */
  zoomOut(zoom: number, step: number, min: number): number {
    return this.clamp(Math.min(zoom - 1, zoom / step), min, zoom);
  }

  /**
   * Zoom percentage that fits `media` inside `stage`, accounting for rotation by swapping the
   * media's width/height at 90°/270°. Never magnifies past 100%.
   */
  fitToZoom(
    media: MediaSize,
    stage: StageSize,
    rotationDegrees: number,
    min: number,
    max: number
  ): number {
    const rotated = rotationDegrees === 90 || rotationDegrees === 270;
    const mediaWidth = rotated ? media.height : media.width;
    const mediaHeight = rotated ? media.width : media.height;
    if (mediaWidth <= 0 || mediaHeight <= 0) {
      return this.clamp(100, min, max);
    }

    const availableWidth = Math.max(1, stage.width - this.gutter);
    const availableHeight = Math.max(1, stage.height - this.gutter);
    const scale = Math.min(availableWidth / mediaWidth, availableHeight / mediaHeight) * 100;
    return this.clamp(Math.min(100, scale), min, max);
  }

  /** Convert a zoom percentage to a CSS transform scale factor. */
  toScale(zoom: number): number {
    return zoom / 100;
  }
}
